// @ts-strict-ignore
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { UserModel } from '../../model/user.model';
import { UserProfileVO } from '../../model/vo/user.vo';
import { UserModelService } from '../../model/user.model.service';
import { UserSessionService } from '../../service/user-session.service';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { SystemNotificationManager } from '@project/components/article/manager/system-notification.manager';
import { NotificationVO } from '@shared/vo/notification.vo';
import { InitManager } from '@shared/manager/init.manager';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Component that can be used for a login without navigating to the login page
 */
@Component({
  standalone: false,
  selector: 'app-relogin',
  templateUrl: './relogin.component.html',
})
export class ReloginComponent implements OnInit, OnDestroy {

  private static readonly DELAY_OPENING_NEXT_WINDOW: number = 500;


  public localAuthenticationEnabled: boolean = false;
  public cdcEnabled: boolean = false;
  public cdcInitialized: boolean = false;

  public loginNotifications: NotificationVO[] = [];
  public hasTemporaryLoginNotifications: boolean = false;

  public form: FormGroup;
  public isGuest: boolean;

  @Input() closable: boolean = true;
  @Input() showHeaderLogin: boolean;
  @Output() public finish = new EventEmitter<void>();

  private _subscriptions: Subscription = null;

  constructor(
    private cdRef: ChangeDetectorRef,
    private systemConfiguration: SystemConfigurationService,
    private systemNotificationManager: SystemNotificationManager,
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    userModelService: UserModelService,
    private userSessionService: UserSessionService
  ) {
    userModelService.loginChanged$.pipe(takeUntilDestroyed()).subscribe(() => this.loginHandler());
  }

  public ngOnInit() {
    this.updateNotifications();
    this._subscriptions = new Subscription();
    this._subscriptions.add(UserSessionService.cdcEnabled.subscribe((cdcEnabled: boolean) => {
      this.cdcEnabled = cdcEnabled;
      this.cdRef.markForCheck();
    }));
    this._subscriptions.add(InitManager.localAuthenticationEnabled.subscribe((localAuthenticationEnabled: boolean) => {
      this.localAuthenticationEnabled = localAuthenticationEnabled;
      this.cdRef.markForCheck();
    }));
    this._subscriptions.add(UserSessionService.cdcInitialized.subscribe((cdcInitialized: boolean) => {
      this.cdcInitialized = cdcInitialized;
      this.cdRef.markForCheck();
    }));
    this.isGuest = this.userModel.isGuestUser(this.userModel.userProfile);
    this.form = this.formBuilder.group({
      username: [''],
      password: [''],
    });
  }

  public ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
      this._subscriptions = null;
    }
  }

  private loginHandler(): void {
    const userProfile: UserProfileVO = this.userModel.userProfile;
    if (userProfile && userProfile.isRegisteredUserAccount()) {
      setTimeout(() => this.close(), ReloginComponent.DELAY_OPENING_NEXT_WINDOW);
    }
  }

  public close() {
    this.bsModalRef.hide();
    this.finish.emit();
  }

  public cdcLogin(): Promise<void> {
    return this.login(true);
  }

  public async login(cdcLogin: boolean = false): Promise<void> {
    if (cdcLogin) {
      (<any>window).gigya.sso.login({authFlow: 'popup'});
    } else {
      await this.userSessionService.login(this.form.controls['username'].value, this.form.controls['password'].value);
    }
  }

  public async onEnter(): Promise<void> {
    await this.login();
  }

  public cdcSignUp(): void {
    window.open(this.systemConfiguration.currentConfiguration.cdcRegisterUserUrl, '_blank');
  }

  public getNotificationText(notification: NotificationVO): string {
    return this.systemNotificationManager.getNotificationText(notification);
  }

  public async updateNotifications(): Promise<void> {
    const notifications = await this.systemNotificationManager.getNotificationsForLocation("login");
    this.loginNotifications = notifications;
    this.hasTemporaryLoginNotifications = this.systemNotificationManager.includesTemporaryNotification(notifications);
    this.cdRef.markForCheck();
  }

  public confirmNotifications(): Promise<void> {
    this.systemNotificationManager.applyConfirmationToAllNotificationsAtLocation("login");
    return this.updateNotifications();
  }

}
